import React, { useCallback } from "react";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";

// Components
import Typography from "../Typography";
import {
    ContainerComponent,
    ControlComponent,
    DropdownIndicatorComponent,
    DropdownIndicatorComponentWrapper,
    InputComponent,
    NoOptionsMessageComponent,
    OptionComponent,
    SingleValueComponent,
    PlaceholderComponent,
    ValueComponent,
    ClearIconComponent,
} from "../Select/RSelectDerived";
import Flex from "../../layout/Flex";

// Types
import SearchProps from "./Search.types";

// Style
import { StyledSelectWrapper } from "../Select/Select.styles";
import COLORS from "../../constants/colors";

const Search = React.forwardRef((props: SearchProps, ref: any) => {
    const {
        id = props?.name,
        label,
        errorMsg,
        option,
        required,
        noOptionText,
        noOptionHandler,
        labelFormatter,
        isClearable = false,
        value = null,
        outline,
        customDropDownIcon,
        customSeparatorIndicator,
        debounceDuration = 500,
        loadOptions,
        menuPosition = "fixed",
        borderRadius,
        size = "md",
        ...restProps
    } = props;

    const debouncedFetchData = useCallback(
        debounce((query, cb) => {
            loadOptions?.(query, cb);
        }, debounceDuration),
        [],
    );

    const CustomDropdown = (props: any) => {
        return (
            <DropdownIndicatorComponentWrapper {...props}>
                {customDropDownIcon}
            </DropdownIndicatorComponentWrapper>
        );
    };

    const MainChild = (
        <>
            <AsyncSelect
                {...restProps}
                ref={ref}
                id={`${id}__container`}
                inputId={`${id}__input`}
                instanceId={id}
                options={option}
                loadOptions={debouncedFetchData}
                components={{
                    DropdownIndicator:
                        CustomDropdown ?? DropdownIndicatorComponent,
                    SelectContainer: ContainerComponent,
                    Control: ControlComponent,
                    SingleValue: SingleValueComponent,
                    NoOptionsMessage: NoOptionsMessageComponent,
                    Option: OptionComponent,
                    Input: InputComponent,
                    IndicatorSeparator:
                        (customSeparatorIndicator as any) ?? null,
                    ValueContainer: ValueComponent,
                    Placeholder: PlaceholderComponent,
                    ClearIndicator: ClearIconComponent,
                    ...(props?.components as any),
                }}
                noOptionsMessage={
                    noOptionHandler ?? (() => noOptionText ?? null)
                    // ?? "No Option"
                }
                formatOptionLabel={labelFormatter}
                isClearable={isClearable}
                value={value}
                menuPosition={menuPosition} // experimental fix
            />
        </>
    );

    const BaseStyled = (
        <Flex flexDirection="column" gap={12}>
            <Typography as="label" color={COLORS.GREY_900} required={required}>
                {label}
            </Typography>

            <StyledSelectWrapper outline={outline} size={size}>
                {MainChild}
            </StyledSelectWrapper>
            <Typography color={COLORS.RED} size="s">
                {errorMsg}
            </Typography>
        </Flex>
    );

    return props?.components ? MainChild : BaseStyled;
});

export default Search;
